import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatINR } from '../../../utils/currency';

const StatusSummaryCards = ({ assets = [], onSelectStatus, activeFilterStatus = 'all' }) => {
  // Calculate status counts and values dynamically from assets
  const getStatusMetrics = () => {
    const defaultStatuses = {
      active: {
        id: 'active',
        title: 'Active Assets',
        count: 0,
        value: 0,
        icon: 'CheckCircle',
        color: 'success',
        description: 'Currently deployed & operational'
      },
      spare: {
        id: 'spare',
        title: 'Spare Inventory',
        count: 0,
        value: 0,
        icon: 'Package',
        color: 'accent',
        description: 'Available for deployment'
      },
      repair: {
        id: 'repair',
        title: 'Under Repair',
        count: 0,
        value: 0,
        icon: 'Wrench',
        color: 'warning',
        description: 'Currently being serviced'
      },
      retired: {
        id: 'retired',
        title: 'Retired Assets',
        count: 0,
        value: 0,
        icon: 'Archive',
        color: 'secondary',
        description: 'End of lifecycle or disposed'
      }
    };

    // Tally real data
    assets.forEach(asset => {
      const st = (asset.status || 'spare').toLowerCase().trim();
      const price = parseFloat(asset.purchase_price) || parseFloat(asset.price) || 0;
      
      if (!defaultStatuses[st]) {
        defaultStatuses[st] = {
          id: st,
          title: st.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) + ' Assets',
          count: 0,
          value: 0,
          icon: 'Layers',
          color: 'primary',
          description: `Assets in ${st} status`
        };
      }
      
      defaultStatuses[st].count++;
      defaultStatuses[st].value += price;
    });

    return defaultStatuses;
  };

  const statusMetrics = getStatusMetrics();
  const totalAssets = assets.length || 1;

  // We display the core 4 statuses (plus any dynamic ones if they have count > 0)
  const statusData = Object.values(statusMetrics).map(st => ({
    ...st,
    percentage: ((st.count / totalAssets) * 100).toFixed(1),
  }));

  const getColorClasses = (color) => {
    switch (color) {
      case 'success':
        return { bg: 'bg-success/10', text: 'text-success', border: 'border-success/20', icon: 'text-success' };
      case 'warning':
        return { bg: 'bg-warning/10', text: 'text-warning', border: 'border-warning/20', icon: 'text-warning' };
      case 'accent':
        return { bg: 'bg-accent/10', text: 'text-accent', border: 'border-accent/20', icon: 'text-accent' };
      case 'secondary':
        return { bg: 'bg-secondary/10', text: 'text-secondary', border: 'border-secondary/20', icon: 'text-secondary' };
      default:
        return { bg: 'bg-primary/10', text: 'text-primary', border: 'border-primary/20', icon: 'text-primary' };
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
      {statusData?.map((status) => {
        const colors = getColorClasses(status?.color);
        const isSelected = activeFilterStatus === status.id;
        
        return (
          <div
            key={status?.id}
            onClick={() => onSelectStatus?.(status.id)}
            className={`bg-card border rounded-lg p-6 shadow-subtle hover:shadow-moderate transition-smooth cursor-pointer group ${
              isSelected ? 'border-primary ring-2 ring-primary/20 bg-primary/[0.02]' : 'border-border'
            }`}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center border ${colors?.bg} ${colors?.border}`}>
                <Icon name={status?.icon} size={24} className={colors?.icon} />
              </div>
              
              <div className="text-right">
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-muted border border-border text-foreground">
                  {formatINR(status.value, true)}
                </span>
              </div>
            </div>
            
            {/* Main Content */}
            <div className="mb-4">
              <div className="flex items-baseline space-x-2 mb-1">
                <h3 className="text-3xl font-bold text-card-foreground">
                  {status?.count}
                </h3>
                <span className="text-sm font-medium text-muted-foreground">
                  ({status?.percentage}%)
                </span>
              </div>
              <p className="text-sm font-medium text-card-foreground mb-1">
                {status?.title}
              </p>
              <p className="text-xs text-muted-foreground">
                {status?.description}
              </p>
            </div>

            {/* Total Valuation in INR */}
            <div className="pt-3 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
              <span>Book Value:</span>
              <span className="font-semibold text-card-foreground">
                {formatINR(status.value, false)}
              </span>
            </div>
            
            {/* Action Button */}
            <div className="mt-3 flex items-center justify-between text-xs font-medium text-primary group-hover:underline">
              <span>{isSelected ? 'Filter Active' : 'Filter by this status'}</span>
              <Icon name="ArrowRight" size={14} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default StatusSummaryCards;
